import React, { useState } from 'react';
import { Button, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Share2, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import shareLinksService from '../../services/shareLinksService';
import { getMarketingOrigin } from '../../utils/marketingUrl';

export default function ShareButton({
  resourceType,
  resourceId,
  resourceTitle,
  variant = "outline-primary",
  size = "sm",
  className = "",
  children,
  autoGenerate = false,
  iconOnly = false,
  shareUrl = null, // Pre-generated share URL from backend
  shareToken = null, // Pre-generated share token from backend
  // The signed-in caller owns this resource, so mint through the AUTHED route
  // and ask for a link that does not expire. The server does not take our word
  // for the ownership -- it re-proves it and clamps the TTL back to the default
  // if it cannot (see ShareLinksAdminController.groupOwnsListing) -- so this is
  // a request, not a grant.
  //
  // Off everywhere by default, which is what keeps the anonymous surfaces
  // (swipe deck, map, share landing) on the public endpoint: they have no token
  // to mint with, so routing them through the authed one would 401 every share.
  ownerMint = false,
  // The no-link-yet mode. A caller supplying this is saying: there is
  // deliberately no link for this resource, I know it, and here is what the tap
  // should do instead of minting one. The anonymous /providers door is the case
  // it exists for -- a real card the visitor built, which cannot have a link
  // until they have an account to hang it on.
  //
  // Opt-in on purpose. Without it the "no link, no button" rule below still
  // holds everywhere, so a share button that cannot share stays impossible
  // unless somebody explicitly asks for one.
  onRequestShare = null,
  // Only the label is overridable through `children`; the tooltip was fixed at
  // "Copy share link", which is a lie beside a tap that copies nothing.
  tooltipText = null
}) {
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const resolveProvidedUrl = () => {
    if (shareUrl) return shareUrl;
    if (shareToken) return `${getMarketingOrigin()}/s/${shareToken}`;
    return null;
  };

  // Provider cards are pre-minted upstream: the company deck and the anonymous
  // deck both attach a shareUrl per row, and a row whose card cannot be shared
  // arrives without one. Without a link there is nothing to copy, and minting
  // on tap here is what produced "This provider has not turned on card sharing
  // yet" as a browser alert. That sentence was correct and useless: it
  // restated a setting on somebody else's account to a person who could do
  // nothing about it, and told a company something about a provider that is
  // the provider's business. No link, no button.
  //
  // Other resource types still mint on demand; only provider cards are
  // pre-minted, so only they take this branch.
  //
  // onRequestShare is the one exception: with a handler there IS defined
  // behaviour without a url, so the absent link is no longer a dead end.
  const requestOnly = Boolean(onRequestShare) && !resolveProvidedUrl();
  if (resourceType === 'crnaProfile' && !resolveProvidedUrl() && !requestOnly) return null;

  const handleClick = async (e) => {
    e.stopPropagation();

    if (loading) return;

    // Before the spinner and before either endpoint: there is nothing to wait
    // for, and minting a crnaProfile from here would hit the AUTHED route and
    // 401 the very visitor this mode exists to serve.
    if (requestOnly) {
      onRequestShare(e);
      return;
    }

    setLoading(true);
    try {
      const providedUrl = resolveProvidedUrl();

      if (providedUrl) {
        await shareLinksService.copyToClipboard(providedUrl);
      } else {
        // Provider cards mint through the AUTHED endpoint. The public one
        // refuses `crnaProfile` with a 403 on purpose: it takes a
        // caller-supplied resourceId with no auth, and the anonymous card
        // sanitiser hands the ProviderProfile id out with every shared card, so
        // accepting them there let anyone who opened one card mint unlimited
        // links to it. Sending provider cards to the public endpoint was a
        // guaranteed failure, which is what the "Failed to create share link"
        // alert on the profile preview actually was.
        //
        // The anonymous provider decks are unaffected: those rows arrive with a
        // machine-minted shareUrl, so they take the providedUrl branch above and
        // never reach either endpoint.
        const result =
          resourceType === 'groupListing' && ownerMint
            ? await shareLinksService.createOwnedListingLink(resourceId)
            : await (
                resourceType === 'crnaProfile'
                  ? shareLinksService.createShareLink
                  : shareLinksService.createPublicShareLink
              ).call(shareLinksService, resourceType, resourceId, {
                singleUse: false,
                expiresInSec: 7 * 24 * 60 * 60
              });

        // Copy to clipboard
        await shareLinksService.copyToClipboard(result.url);
      }

      // Show success feedback
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to create share link:', err);
      // The server says what is wrong and often what to do about it ("Agree to
      // sharing your card before you send the link", "Your card is paused").
      // Replacing that with a generic sentence was hiding the fix from the
      // person who could apply it.
      // A network or clipboard error should not freeze the entire Listings
      // page behind a native browser alert. Keep the inspector open and let the
      // recruiter retry in place.
      toast.error(err?.message || 'Failed to create share link');
    } finally {
      setLoading(false);
    }
  };

  // Determine the display content (Share icon or Copied checkmark)
  const displayContent = copied ? (
    <>
      <Check size={14} strokeWidth={2.5} />
      {!iconOnly && 'Copied!'}
    </>
  ) : (
    <>
      <Share2 size={14} strokeWidth={2} />
      {/* "Copy card link" rather than "Share" on a provider card: the button
          copies, it does not open a share sheet, and the recruiter pressing it
          is passing the card to a colleague rather than publishing anything. */}
      {!iconOnly &&
        (children ||
          (resourceType === 'crnaProfile' && !requestOnly ? 'Copy card link' : 'Share'))}
    </>
  );

  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{copied ? 'Link copied!' : tooltipText || 'Copy share link'}</Tooltip>}
    >
      <Button
        variant={variant}
        size={size}
        className={`d-flex align-items-center gap-2 ${className}`}
        onClick={handleClick}
        disabled={loading}
        style={{
          borderRadius: '8px',
          padding: iconOnly ? '6px' : '4px 10px',
          fontWeight: '500',
          position: 'relative',
          transition: 'all 0.2s ease',
          ...(copied ? {
            color: '#16a34a',
            borderColor: '#16a34a',
            background: 'rgba(22, 163, 74, 0.08)',
          } : {})
        }}
      >
        {/* Keep content for consistent sizing, hide when loading */}
        <span style={{ visibility: loading ? 'hidden' : 'visible', display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.75rem' }}>
          {displayContent}
        </span>
        {/* Overlay spinner when loading */}
        {loading && (
          <span style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}>
            <Spinner animation="border" size="sm" />
          </span>
        )}
      </Button>
    </OverlayTrigger>
  );
}
